import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Search, ShieldCheck, AlertTriangle, PauseCircle, Ban, Eye, Trash2, X, Bell, Check, ArrowRight, MessageCircle, Link2, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { formatItalianDateTime } from "../../lib/dateUtils";
import { Button, Card, Spinner, StatusBadge, Select, PageGuide } from "../../components/ui-kit";
import { AdminQrHealthPanel } from "../../components/QrHealthPanel";

const STATUS = { draft: { s: "grigio", l: "Bozza" }, active: { s: "verde", l: "Attiva" }, limited: { s: "giallo", l: "Limitata" }, blocked: { s: "rosso", l: "Bloccata" } };

// Sede (o sedi) sotto la ragione sociale nella scheda azienda: con più sedi mostra le prime due e il numero delle altre.
function SitesLine({ sites = [] }) {
  if (!sites.length) return <div className="mt-0.5 flex items-center gap-1 text-xs text-red-600"><MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Nessuna sede inserita</div>;
  const label = sites.length === 1
    ? [sites[0].name, sites[0].address].filter(Boolean).join(" — ")
    : `${sites.slice(0, 2).map((s) => s.name).join(", ")}${sites.length > 2 ? ` +${sites.length - 2}` : ""}`;
  const full = sites.map((s) => [s.name, s.address].filter(Boolean).join(" — ")).join("\n");
  return <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-600" title={full} data-testid="tenant-card-sites"><MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" /><span className="truncate">{label}</span></div>;
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fSem, setFSem] = useState("");
  const [onlyWithNc, setOnlyWithNc] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activationRequests, setActivationRequests] = useState([]);
  const [resolvingRequest, setResolvingRequest] = useState(null);
  const [missingInfoUsers, setMissingInfoUsers] = useState([]);
  const [incompleteClientDocs, setIncompleteClientDocs] = useState([]);
  const [resendingDocs, setResendingDocs] = useState(null);
  const [conformity, setConformity] = useState(null);

  const loadActivationRequests = () =>
    api.get("/admin/activation-requests").then(({ data }) => setActivationRequests(data.requests)).catch(() => {});
  const loadIncompleteClientDocs = () =>
    api.get("/admin/client-docs/missing").then(({ data }) => setIncompleteClientDocs(data.tenants)).catch(() => {});

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setStats(data.stats)).catch(() => {});
    api.get("/admin/tenants").then(({ data }) => setTenants(data.tenants)).catch(() => setTenants([]));
    loadActivationRequests();
    api.get("/admin/users/missing-info").then(({ data }) => setMissingInfoUsers(data.users)).catch(() => {});
    loadIncompleteClientDocs();
    api.get("/admin/conformity-overview").then(({ data }) => setConformity(data.tenants)).catch(() => setConformity({}));
  }, []);

  const resendClientDocsLink = async (t) => {
    setResendingDocs(t.tenant_id);
    try {
      const { data: res } = await api.post(`/admin/tenants/${t.tenant_id}/client-doc-link`);
      const url = `${window.location.origin}/documenti/${res.token}`;
      const waNumber = (raw) => (raw || "").replace(/\D/g, "");
      const text = `Ciao, per completare la tua pratica HACCP mancano ancora questi documenti: ${t.missing_labels.join(", ")}. Puoi caricarli qui: ${url}`;
      window.open(`https://wa.me/${waNumber(t.whatsapp)}?text=${encodeURIComponent(text)}`, "_blank");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResendingDocs(null); }
  };

  const resolveActivationRequest = async (req) => {
    setResolvingRequest(req.id);
    try {
      await api.post(`/admin/activation-requests/${req.id}/resolve`);
      setActivationRequests((current) => current.filter((r) => r.id !== req.id));
      toast.success("Richiesta segnata come gestita");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResolvingRequest(null); }
  };

  useEffect(() => {
    if (!tenantToDelete) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !deleting) setTenantToDelete(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [tenantToDelete, deleting]);

  const deleteTenant = async () => {
    if (!tenantToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/tenants/${tenantToDelete.id}`);
      setTenants((current) => current?.filter((tenant) => tenant.id !== tenantToDelete.id) || []);
      const { data } = await api.get("/admin/dashboard");
      setStats(data.stats);
      toast.success(`Azienda “${tenantToDelete.name}” eliminata`);
      setTenantToDelete(null);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Impossibile eliminare l'azienda");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!tenants) return [];
    return tenants.filter((t) =>
      (!q || t.name.toLowerCase().includes(q.toLowerCase()) || (t.sites || []).some((s) => `${s.name} ${s.address}`.toLowerCase().includes(q.toLowerCase()))) &&
      (!fStatus || t.status === fStatus) &&
      (!fSem || t.semaforo === fSem) &&
      (!onlyWithNc || t.nc_open > 0));
  }, [tenants, q, fStatus, fSem, onlyWithNc]);

  const showCompanies = (view) => {
    setQ("");
    setFSem("");
    setOnlyWithNc(view === "nc");
    setFStatus(["active", "limited", "blocked"].includes(view) ? view : "");
    window.setTimeout(() => document.getElementById("admin-companies")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const cards = stats ? [
    { label: "Aziende totali", value: stats.tenants, Icon: Building2, color: "text-navy", bg: "bg-slate-50", view: "all", actionClass: "text-navy hover:bg-slate-100 focus:ring-slate-300" },
    { label: "Attive", value: stats.active, Icon: ShieldCheck, color: "text-emerald-600", bg: "bg-emerald-50", view: "active", actionClass: "text-emerald-700 hover:bg-emerald-100 focus:ring-emerald-300" },
    { label: "Limitate", value: stats.limited, Icon: PauseCircle, color: "text-amber-600", bg: "bg-amber-50", view: "limited", actionClass: "text-amber-700 hover:bg-amber-100 focus:ring-amber-300" },
    { label: "Bloccate", value: stats.blocked, Icon: Ban, color: "text-red-600", bg: "bg-red-50", view: "blocked", actionClass: "text-red-700 hover:bg-red-100 focus:ring-red-300" },
    { label: "Non conformità aperte", value: stats.nc_open, Icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50", view: "nc", actionClass: "text-red-700 hover:bg-red-100 focus:ring-red-300" },
  ] : [];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-body">Consulente · DF Consulting Group</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold">Dashboard globale</h1>
        <p className="text-slate-500 font-body mt-1">Tutte le aziende, i livelli di consulenza, i moduli e le criticità in un'unica vista.</p>
      </div>

      <PageGuide
        title="Guida rapida: Dashboard globale"
        summary="Questo è il punto di partenza per gestire tutte le aziende clienti: da qui individui cosa richiede attenzione e apri la configurazione di ciascuna azienda."
        goals={[
          "Avere sotto controllo lo stato di tutte le aziende clienti: attive, limitate, bloccate.",
          "Individuare rapidamente le aziende con non conformità aperte da seguire.",
          "Aprire la scheda di configurazione completa di un'azienda specifica.",
        ]}
        steps={[
          "Tocca “Visualizza” su una card in alto per filtrare l'elenco sottostante (es. solo aziende limitate, o solo con non conformità).",
          "Usa la ricerca e i filtri di stato/semaforo per trovare un'azienda per nome o per situazione di conformità.",
          "Tocca una card azienda per aprirne la scheda completa; usa “Nuova azienda” in alto per avviare l'onboarding di un nuovo cliente.",
        ]}
        testid="dashboard-guide"
      />

      <AdminQrHealthPanel />

      {missingInfoUsers.length > 0 && (
        <Card className="p-5 border-blue-200 bg-blue-50" data-testid="missing-info-banner">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-5 h-5 text-blue-600" />
            <h2 className="font-heading font-semibold text-blue-900">
              {missingInfoUsers.length} {missingInfoUsers.length === 1 ? "utente" : "utenti"} senza email e/o firma
            </h2>
          </div>
          <div className="space-y-2">
            {missingInfoUsers.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-white p-3" data-testid={`missing-info-${u.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-navy">{u.name} <span className="text-slate-400 font-normal">— {u.tenant_name} · {u.role_label}</span></div>
                  <div className="text-xs text-slate-400">
                    Manca: {[u.missing_email && "email", u.missing_signature && "firma"].filter(Boolean).join(" e ")}
                  </div>
                </div>
                <Button variant="outline" onClick={() => nav(`/admin/tenants/${u.tenant_id}?tab=utenti`)} data-testid={`missing-info-open-${u.id}`}>
                  <ArrowRight className="w-4 h-4" /> Vai al cliente
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {incompleteClientDocs.length > 0 && (
        <Card className="p-5 border-red-200 bg-red-50" data-testid="incomplete-client-docs-banner">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-5 h-5 text-red-600" />
            <h2 className="font-heading font-semibold text-red-900">
              {incompleteClientDocs.length} {incompleteClientDocs.length === 1 ? "modulo documenti incompleto" : "moduli documenti incompleti"}
            </h2>
          </div>
          <div className="space-y-2">
            {incompleteClientDocs.map((t) => (
              <div key={t.tenant_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-white p-3" data-testid={`incomplete-client-docs-${t.tenant_id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-navy">{t.tenant_name}</div>
                  <div className="text-xs text-slate-400">Mancano: {t.missing_labels.join(", ")}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" onClick={() => nav(`/admin/tenants/${t.tenant_id}?tab=documenti-cliente`)} data-testid={`incomplete-client-docs-open-${t.tenant_id}`}>
                    <ArrowRight className="w-4 h-4" /> Vai al cliente
                  </Button>
                  {t.whatsapp && (
                    <Button variant="success" loading={resendingDocs === t.tenant_id} onClick={() => resendClientDocsLink(t)} data-testid={`incomplete-client-docs-resend-${t.tenant_id}`}>
                      <MessageCircle className="w-4 h-4" /> Rimanda su WhatsApp
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {activationRequests.length > 0 && (
        <Card className="p-5 border-amber-200 bg-amber-50" data-testid="activation-requests-banner">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-5 h-5 text-amber-600" />
            <h2 className="font-heading font-semibold text-amber-900">
              {activationRequests.length} {activationRequests.length === 1 ? "richiesta di attivazione" : "richieste di attivazione"} in attesa
            </h2>
          </div>
          <div className="space-y-2">
            {activationRequests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white p-3" data-testid={`activation-request-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-navy">{r.tenant_name} <span className="text-slate-400 font-normal">— {r.module_label}</span></div>
                  <div className="text-xs text-slate-400">Richiesto da {r.requested_by_name || "utente"} il {formatItalianDateTime(r.created_at)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" onClick={() => nav(`/admin/tenants/${r.tenant_id}?tab=moduli`)} data-testid={`activation-request-open-${r.id}`}>
                    <ArrowRight className="w-4 h-4" /> Vai al cliente
                  </Button>
                  <Button variant="success" loading={resolvingRequest === r.id} onClick={() => resolveActivationRequest(r)} data-testid={`activation-request-resolve-${r.id}`}>
                    <Check className="w-4 h-4" /> Segna come gestita
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!stats ? <Spinner /> : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {cards.map((c) => (
            <Card key={c.label} className={`p-3.5 sm:p-4 flex flex-col ${((c.view === fStatus) || (c.view === "nc" && onlyWithNc)) ? "ring-2 ring-offset-1 ring-gold" : ""}`} data-testid={`admin-stat-${c.label}`}>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${c.bg}`}><c.Icon className={`w-4 h-4 ${c.color}`} strokeWidth={1.8} /></div>
                <div className="font-heading text-2xl sm:text-3xl leading-none font-bold text-navy">{c.value}</div>
              </div>
              <div className="text-xs text-slate-500 mt-2 font-body leading-tight min-h-[30px]">{c.label}</div>
              <button
                type="button"
                onClick={() => showCompanies(c.view)}
                className={`mt-1 -ml-1 self-start inline-flex min-h-[34px] items-center gap-1.5 rounded-md px-1 text-[11px] sm:text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${c.actionClass}`}
                aria-label={`Visualizza ${c.label.toLowerCase()}`}
                data-testid={`show-${c.view}-tenants`}
              >
                <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                Visualizza
              </button>
            </Card>
          ))}
        </div>
      )}

      <div id="admin-companies" className="scroll-mt-6">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold">
              {onlyWithNc ? "Aziende con non conformità aperte" : fStatus === "active" ? "Aziende attive" : fStatus === "limited" ? "Aziende limitate" : fStatus === "blocked" ? "Aziende bloccate" : "Aziende"}
            </h2>
            {(fStatus || onlyWithNc) && (
              <button type="button" onClick={() => showCompanies("all")} className="mt-1 text-sm text-navy underline underline-offset-4 hover:text-navy-600">
                Mostra tutte le aziende
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-56">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca azienda o sede…" className="min-h-[44px] pl-10 pr-4 rounded-lg border border-hairline bg-white text-sm w-full" data-testid="admin-search" />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:flex">
              <Select value={fStatus} onChange={(e) => { setOnlyWithNc(false); setFStatus(e.target.value); }} className="sm:w-40" data-testid="admin-filter-status"><option value="">Tutti gli stati</option><option value="active">Attive</option><option value="limited">Limitate</option><option value="blocked">Bloccate</option></Select>
              <Select value={fSem} onChange={(e) => setFSem(e.target.value)} className="sm:w-40" data-testid="admin-filter-sem"><option value="">Tutti i semafori</option><option value="verde">Verde</option><option value="giallo">Giallo</option><option value="rosso">Rosso</option><option value="grigio">Grigio</option></Select>
            </div>
          </div>
        </div>

        {!tenants ? <Spinner /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((t) => (
              <Card key={t.id} className="p-5 hover:shadow-cardHover transition-shadow cursor-pointer" onClick={() => nav(t.status === "draft" ? `/admin/onboarding/${t.id}` : `/admin/tenants/${t.id}`)} data-testid={`tenant-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-navy grid place-items-center shrink-0 text-gold-bright font-heading font-bold">{t.name[0]}</div>
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-navy break-words sm:truncate">{t.name}</div>
                      <SitesLine sites={t.sites} />
                      <div className="text-xs text-slate-400">Consulenza {t.plan === "SUPER_PREMIUM" ? "Super Premium" : t.plan.charAt(0) + t.plan.slice(1).toLowerCase()}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={STATUS[t.status]?.s}>{STATUS[t.status]?.l || "Stato non definito"}</StatusBadge>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setTenantToDelete(t);
                      }}
                      className="w-10 h-10 rounded-full border border-red-200 bg-red-50 text-red-600 grid place-items-center hover:bg-red-100 hover:border-red-300 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                      aria-label={`Elimina azienda ${t.name}`}
                      title="Elimina azienda"
                      data-testid={`delete-tenant-${t.id}`}
                    >
                      <Trash2 className="w-4.5 h-4.5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3 text-slate-500">
                    <span className="inline-flex items-center gap-1"><Building2 className="w-4 h-4" /> {t.sites_count} sedi</span>
                    <span className="inline-flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {t.nc_open} NC</span>
                  </div>
                  <StatusBadge status={t.semaforo} testid={`tenant-sem-${t.id}`} />
                </div>
                {["active", "limited"].includes(t.status) && <ConformityLine data={conformity?.[t.id]} loading={!conformity} testid={`tenant-conformity-${t.id}`} />}
                {t.status === "draft" && (
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-medium text-amber-700">Configurazione da completare — tocca per riprendere</p>
                    <button type="button" onClick={(event) => { event.stopPropagation(); nav(`/admin/tenants/${t.id}?tab=documenti-cliente`); }}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                      data-testid={`draft-client-links-${t.id}`}>
                      <Link2 className="h-4 w-4" aria-hidden="true" /> Link al cliente
                    </button>
                  </div>
                )}
              </Card>
            ))}
            {filtered.length === 0 && <p className="text-slate-400 text-sm col-span-full">Nessuna azienda trovata.</p>}
          </div>
        )}
      </div>

      {tenantToDelete && (
        <div
          className="fixed inset-0 z-50 bg-navy/55 backdrop-blur-sm flex items-center justify-center p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !deleting) setTenantToDelete(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-tenant-title"
            aria-describedby="delete-tenant-description"
            className="relative w-full max-w-lg rounded-2xl bg-white p-6 sm:p-8 shadow-2xl border border-red-100"
            data-testid="delete-tenant-dialog"
          >
            <button
              type="button"
              onClick={() => setTenantToDelete(null)}
              disabled={deleting}
              className="absolute right-4 top-4 w-10 h-10 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              aria-label="Chiudi conferma eliminazione"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>

            <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 grid place-items-center mb-5">
              <Trash2 className="w-7 h-7" aria-hidden="true" />
            </div>
            <h2 id="delete-tenant-title" className="font-heading text-2xl font-bold text-navy pr-10">Eliminare definitivamente l'azienda?</h2>
            <p id="delete-tenant-description" className="font-body text-slate-600 mt-3 leading-relaxed">
              Stai per eliminare <strong className="text-navy">{tenantToDelete.name}</strong> insieme a sedi, reparti, utenti e registrazioni collegate. Questa operazione non può essere annullata.
            </p>

            <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setTenantToDelete(null)} disabled={deleting}>
                Annulla
              </Button>
              <Button type="button" variant="danger" onClick={deleteTenant} loading={deleting}>
                <Trash2 className="w-4 h-4" aria-hidden="true" />
                Sì, elimina definitivamente
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const percentTone = (value) => (value == null ? "text-slate-400" : value >= 95 ? "text-emerald-700" : value >= 80 ? "text-amber-700" : "text-red-600");

function ConformityLine({ data, loading, testid }) {
  if (loading) return <div className="mt-2 text-xs text-slate-400">Calcolo conformità del mese…</div>;
  if (!data || (data.completion == null && data.conformity == null)) return <div className="mt-2 text-xs text-slate-400" data-testid={testid}>Questo mese: nessun controllo previsto finora</div>;
  return (
    <div className="mt-2 text-xs text-slate-500" data-testid={testid} title="Stesso calcolo del quadro conformità mostrato all'ispettore (Ispezione ASL/NAS)">
      Questo mese: controlli eseguiti <span className={`font-semibold ${percentTone(data.completion)}`}>{data.completion == null ? "—" : `${data.completion}%`}</span>
      {" · "}esiti conformi <span className={`font-semibold ${percentTone(data.conformity)}`}>{data.conformity == null ? "—" : `${data.conformity}%`}</span>
    </div>
  );
}
